import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { NewsManagerService } from 'src/app/services/news-manager/news-manager.service';
import { NewsManagerModalService } from 'src/app/services/news-manager-modal/news-manager-modal.service';
import { GeneralService } from 'src/app/services/general/general.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-news-manager',
  templateUrl: './add-edit-news-manager.component.html',
  styleUrls: ['./add-edit-news-manager.component.scss']
})
export class AddEditNewsManagerComponent extends DestructorComponent implements OnInit {

  @Output() newsItemAdded = new EventEmitter<void>();
  @Output() newsItemUpdated = new EventEmitter<void>();

  newsManagerForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedNewsItem: any = null;

  // File upload properties
  selectedFile: File | null = null;
  uploadedImageUrl: string = '';
  isUploading = false;

  constructor(
    private fb: FormBuilder,
    private _newsManagerService: NewsManagerService,
    private _newsManagerModalService: NewsManagerModalService,
    private _generalService: GeneralService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.newsManagerForm = this.fb.group({
      show: [true, [Validators.required]],
      start_date: ['', [Validators.required]],
      expiry_date: ['', [Validators.required]],
      news_title: ['', [Validators.required, Validators.minLength(3)]],
      news_description: [''],
      image_url: ['']
    });
  }

  // Getter methods for form controls
  get show() { return this.newsManagerForm.get('show'); }
  get start_date() { return this.newsManagerForm.get('start_date'); }
  get expiry_date() { return this.newsManagerForm.get('expiry_date'); }
  get news_title() { return this.newsManagerForm.get('news_title'); }
  get news_description() { return this.newsManagerForm.get('news_description'); }
  get image_url() { return this.newsManagerForm.get('image_url'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_news_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceNewsItem = this._newsManagerModalService.getSelectedNewsManager();
        if (serviceNewsItem) {
          this.isEditMode = true;
          this.selectedNewsItem = serviceNewsItem;
          this.populateForm(this.selectedNewsItem);
        } else {
          this.isEditMode = false;
          this.selectedNewsItem = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._newsManagerModalService.clearSelectedNewsManager();
        this.resetForm();
      });
    }
  }

  private populateForm(newsItem: any): void {
    this.newsManagerForm.patchValue({
      show: newsItem.show !== undefined ? newsItem.show : true,
      start_date: newsItem.start_date ? this.formatDateForInput(newsItem.start_date) : '',
      expiry_date: newsItem.expiry_date ? this.formatDateForInput(newsItem.expiry_date) : '',
      news_title: newsItem.news_title || '',
      news_description: newsItem.news_description || '',
      image_url: newsItem.image_url || ''
    });

    // Set uploaded image URL for display
    this.uploadedImageUrl = newsItem.image_url || '';
  }

  private formatDateForInput(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  private resetForm(): void {
    this.newsManagerForm.reset({
      show: true,
      start_date: '',
      expiry_date: '',
      news_title: '',
      news_description: '',
      image_url: ''
    });
    this.selectedFile = null;
    this.uploadedImageUrl = '';
    this.isUploading = false;
    this.markFormGroupUntouched(this.newsManagerForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      if (control instanceof FormGroup) {
        this.markFormGroupUntouched(control);
      }
    });
  }

  // File upload methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.uploadFile();
    }
  }

  private uploadFile(): void {
    if (!this.selectedFile) return;

    this.isUploading = true;

    this.subscriptions.push(
      this._generalService.uploadFileByContent({ "contentType": this.selectedFile.type }).subscribe({
        next: (res) => {
          const imageUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, this.selectedFile);

          this.subscriptions.push(
            this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
              next: () => {
                this.uploadedImageUrl = imageUrl;
                this.newsManagerForm.patchValue({ image_url: imageUrl });
                this.isUploading = false;
              },
              error: (error) => {
                console.error('Error uploading to S3:', error);
                this.isUploading = false;
                Swal.fire({
                  text: "Failed to upload image. Please try again.",
                  icon: "error",
                  buttonsStyling: false,
                  confirmButtonText: "Ok, got it!",
                  customClass: {
                    confirmButton: "btn btn-primary"
                  }
                });
              }
            })
          );
        },
        error: (error) => {
          console.error('Error getting presigned URL:', error);
          this.isUploading = false;
          Swal.fire({
            text: "Failed to upload image. Please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  removeImage(): void {
    this.selectedFile = null;
    this.uploadedImageUrl = '';
    this.newsManagerForm.patchValue({ image_url: '' });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_news_manager_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.newsManagerForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.newsManagerForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._newsManagerService.UpdateData(this.selectedNewsItem._id, submitData)
        : this._newsManagerService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('News item operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "News item has been successfully updated!"
                : "News item has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.newsItemUpdated.emit();
                } else {
                  this.newsItemAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('News item operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.newsManagerForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
